import React, { useEffect, useState } from "react";
import Select from "react-select";
import { API_URL , LOCATION_ID } from "..";

const AddPaymentLayer = () => {
  const [suppliers, setSuppliers] = useState([]);
  const [bills, setBills] = useState([]);
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [selectedBill, setSelectedBill] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState(null);
  const [message, setMessage] = useState({ status: false, reason: "", desc: "" });
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Fetch all suppliers
  useEffect(() => {
     const locationId = localStorage.getItem('locationID')
    fetch(`${API_URL}/api/suppliers?locationId=${locationId}`)
      .then((res) => res.json())
      .then((data) => {
        const formatted = data?.suppliers.map((s) => ({ label: s.name, value: s.id }));
        setSuppliers(formatted);
      })
      .catch(() =>
        setMessage({ status: true, reason: "error", desc: "Failed to load suppliers" })
      );
  }, []);

  // Fetch bills when supplier changes
  useEffect(() => {
    if (!selectedSupplier) return;
    fetch(`${API_URL}/api/purchase-bills?supplier_id=${selectedSupplier.value}`)
      .then((res) => res.json())
      .then((data) => {
        const formatted = data.map((b) => ({
          label: `${b.bill_number} (Due: Rs.${b.due_amount})`,
          value: b.id,
        }));
        setBills(formatted);
      })
      .catch(() =>
        setMessage({ status: true, reason: "error", desc: "Failed to load bills" })
      );
  }, [selectedSupplier]);

  const onFormSubmission = async (event) => {
    event.preventDefault();
    setMessage({ status: false, reason: "", desc: "" });

    const formData = {
      supplier_id: selectedSupplier?.value,
      bill_id: selectedBill?.value,
      payment_date: event.target.elements.payment_date.value,
      payment_method: paymentMethod?.value,
      amount: parseFloat(event.target.elements.amount.value || 0),
      remarks: event.target.elements.remarks.value,
    };

    if (!formData.supplier_id) {
      setMessage({ status: true, reason: "error", desc: "Supplier is required" });
      return;
    }
    if (!formData.bill_id) {
      setMessage({ status: true, reason: "error", desc: "Bill is required" });
      return;
    }
    if (!formData.amount || formData.amount <= 0) {
      setMessage({ status: true, reason: "error", desc: "Enter valid amount" });
      return;
    }
    if (!formData.payment_method) {
      setMessage({ status: true, reason: "error", desc: "Select payment method" });
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/api/purchase-payments`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });
      const data = await response.json();

      if (response.ok) {
        setMessage({ status: true, reason: "success", desc: "Payment added successfully" });
        event.target.reset();
        setSelectedSupplier(null);
        setSelectedBill(null);
        setPaymentMethod(null);
      } else {
        throw new Error(data.message || "Failed to add payment");
      }
    } catch (error) {
      setMessage({ status: true, reason: "error", desc: error.message });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="card h-100 p-0 radius-12">
      <div className="card-body p-24">
        <div className="row justify-content-center">
          <div className="col-xxl-8 col-xl-10 col-lg-10">
            <div className="card border">
              <div className="card-body">
                {/* Message Alert */}
                {message.status && (
                  <div
                    className={`alert alert-${
                      message.reason === "error" ? "danger" : "success"
                    } alert-dismissible fade show mb-20`}
                    role="alert"
                  >
                    {message.desc}
                    <button
                      type="button"
                      className="btn-close"
                      onClick={() => setMessage({ ...message, status: false })}
                      aria-label="Close"
                    ></button>
                  </div>
                )}

                {/* Form */}
                <form onSubmit={onFormSubmission}>
                  {/* Supplier */}
                  <div className="mb-20">
                    <label className="form-label fw-semibold text-primary-light text-sm mb-8">
                      Supplier <span className="text-danger-600">*</span>
                    </label>
                    <Select
                      options={suppliers}
                      placeholder="Select Supplier"
                      value={selectedSupplier}
                      onChange={setSelectedSupplier}
                      classNamePrefix="select"
                    />
                  </div>

                  {/* Bill */}
                  <div className="mb-20">
                    <label className="form-label fw-semibold text-primary-light text-sm mb-8">
                      Bill <span className="text-danger-600">*</span>
                    </label>
                    <Select
                      options={bills}
                      placeholder={
                        selectedSupplier
                          ? "Select Bill"
                          : "Select supplier first..."
                      }
                      value={selectedBill}
                      onChange={setSelectedBill}
                      isDisabled={!selectedSupplier}
                      classNamePrefix="select"
                    />
                  </div>

                  {/* Payment Date */}
                  <div className="mb-20">
                    <label className="form-label fw-semibold text-primary-light text-sm mb-8">
                      Payment Date <span className="text-danger-600">*</span>
                    </label>
                    <input
                      type="date"
                      className="form-control radius-8"
                      id="payment_date"
                      name="payment_date"
                      required
                    />
                  </div>

                  {/* Payment Method */}
                  <div className="mb-20">
                    <label className="form-label fw-semibold text-primary-light text-sm mb-8">
                      Payment Method <span className="text-danger-600">*</span>
                    </label>
                    <Select
                      options={[
                        { label: "Cash", value: "Cash" },
                        { label: "Bank", value: "Bank" },
                        { label: "Cheque", value: "Cheque" },
                        { label: "Other", value: "Other" },
                      ]}
                      placeholder="Select Method"
                      value={paymentMethod}
                      onChange={setPaymentMethod}
                      classNamePrefix="select"
                    />
                  </div>

                  {/* Amount */}
                  <div className="mb-20">
                    <label className="form-label fw-semibold text-primary-light text-sm mb-8">
                      Amount (Rs.) <span className="text-danger-600">*</span>
                    </label>
                    <input
                      type="number"
                      className="form-control radius-8"
                      id="amount"
                      name="amount"
                      placeholder="Enter amount"
                      required
                    />
                  </div>

                  {/* Remarks */}
                  <div className="mb-20">
                    <label className="form-label fw-semibold text-primary-light text-sm mb-8">
                      Remarks
                    </label>
                    <textarea
                      className="form-control radius-8"
                      id="remarks"
                      name="remarks"
                      rows="2"
                      placeholder="Optional remarks..."
                    ></textarea>
                  </div>

                  {/* Submit */}
                  <div className="d-flex align-items-center justify-content-center gap-3">
                    <button
                      type="submit"
                      className="btn btn-primary border border-primary-400 text-md px-56 py-12 radius-8"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? "Submitting..." : "Add Payment"}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddPaymentLayer;
